import React, { useState } from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { secondsToTime } from '@soundxyz/utils';
import { FEATURES, PLAYLIST_INFO_ACTIONS } from '@soundxyz/utils/constants/logging';
import { playTrackInPlaylist } from '@components/AudioPlayer/utils';
import { LikeReleaseButton } from '@components/Likes/Button';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { CollectedBadge } from '@components/Playlists/basics/CollectedBadge';
import { getOwnershipStatusTooltipContent } from '@components/Playlists/shared';

import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ViewSongMenuItem } from '@components/basics/Dropdowns/MenuItems/ViewSongMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { SquareImage } from '@components/main-components/image/BaseImage';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { PreviewPlaylistRowFragmentDoc } from '@graphql/reactQuery';
import { useLogInfo } from '@hooks/useLogInfo';
import { trackClickEvent } from '@lib/analytics';
import { loadTrack, useAudioController } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { setPlaylistUpNext } from '@store/audio/AudioFetcher';
import { getTierForNumber } from '@utils/tiers';
import { webappUriPath } from '@utils/url';
import { NewBadge } from './NewBadge';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PreviewPlaylistRow on ShelfRelease {
    id
    release {
      id
      type
      track {
        id
        duration
      }
      artist {
        id
        name
        webappUri
      }
      coverImage {
        url
        id
      }
      staticCoverImage {
        url
        id
      }
      title
      webappUri
      finalQuantity

      ...LikeSoundMenuItem
    }

    ownedTokens {
      serialNumber
      tierNumber
    }
    ownedFirstNft {
      id
      serialNumber
      updatedAt
      tierNumber
    }

    ownedGoldenEgg {
      id
      nft {
        id
        serialNumber
      }
    }
  }
`);

const PreviewRowLink = ({
  pathnameUri,
  children,
}: {
  pathnameUri: Pathname;
  children: React.ReactNode;
}) => {
  return (
    <>
      <AccessibleLink pathnameUri={pathnameUri} className="hidden md:block">
        {children}
      </AccessibleLink>
      <div className="block w-full overflow-hidden text-ellipsis whitespace-nowrap md:hidden">
        {children}
      </div>
    </>
  );
};

export const PreviewRow = ({
  releaseFrag,
  newRelease = false,
  username,
  playlistId,
}: {
  releaseFrag: FragmentType<PreviewPlaylistRowFragmentDoc>;
  newRelease?: boolean;
  username: string;
  playlistId: string;
}) => {
  const logInfo = useLogInfo();
  const { tablet } = MediaQuery.useContainer();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const shelfRelease = getFragment(PreviewPlaylistRowFragmentDoc, releaseFrag);
  const { release, ownedGoldenEgg, ownedTokens, ownedFirstNft } = shelfRelease;
  const {
    id: releaseId,
    artist: { name: artistName, webappUri: artistWebappUri },
    title,
    track: { id: trackId, duration: trackDuration },
    coverImage,
    staticCoverImage,
    webappUri: releaseWebappUri,
    finalQuantity,
    type: releaseType,
  } = release;

  const numNFTsOwned = ownedTokens?.length ?? 0;
  const ownsMultipleNftsForRelease = numNFTsOwned > 1;

  const serialNumber = !!ownedGoldenEgg
    ? ownedGoldenEgg.nft.serialNumber
    : ownedFirstNft?.serialNumber;

  const dateCollected = !!ownedFirstNft?.updatedAt ? new Date(ownedFirstNft.updatedAt) : null;

  const tooltipContent = !!dateCollected
    ? getOwnershipStatusTooltipContent({
        serialNumber,
        finalQuantity,
        username,
        ownsMultipleNftsForRelease,
        numNFTsOwned,
      })
    : null;

  const { playing, activeTrackId } = useAudioController();

  const isActiveTrack = trackId === activeTrackId;
  const isPlaying = isActiveTrack && playing;

  const coverImageUrl = staticCoverImage?.url || coverImage?.url || '';

  const hasAudioAccess = releaseType !== 'ALBUM';
  const { isLiked } = useLikeRelease({ id: releaseId, source: 'Playlist Preview' });

  const onPlayClick = () => {
    logInfo({
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Playlist preview row play button clicked',
      action: PLAYLIST_INFO_ACTIONS.PLAYLIST_PREVIEW_ROW_PLAY_CLICK,
      data: {
        playlistId,
        trackId,
        releaseId,
      },
    });

    if (!isActiveTrack && trackId) {
      loadTrack({ trackId, addToQueue: false });
      setPlaylistUpNext(playlistId, trackId);
    } else {
      togglePlayPause();
    }
  };

  return (
    <div
      className="group/row relative flex h-14 w-[inherit] min-w-0 max-w-full cursor-pointer items-center gap-2 px-4 hover:bg-base50"
      onClick={e => playTrackInPlaylist({ tablet, trackId, isActiveTrack, event: e, playlistId })}
    >
      <div className="mr-auto flex w-full min-w-0 items-center gap-4">
        <div className="relative flex">
          <div className="relative h-10 w-10 overflow-hidden rounded-[6px]">
            <SquareImage size={48} borderRadius={6} src={coverImageUrl} alt={title} />
            {newRelease && (
              <div className="absolute right-0 top-0">
                <NewBadge />
              </div>
            )}
            <PlayButton
              className={clsx(
                'absolute left-0 top-0 group-hover/row:visible',
                isPlaying ? 'visible' : 'invisible',
              )}
              onClick={ev => {
                onPlayClick();
                ev.stopPropagation();
              }}
              playing={isPlaying}
              size="s"
              color="transparent"
              whileTap={undefined}
              whileHover={undefined}
            />
          </div>
        </div>

        <div className="relative flex h-full max-w-[calc(100%-64px)] flex-col items-start justify-center gap-[2px]">
          <div className="flex w-full min-w-full max-w-full items-center">
            {isPlaying && <PlayIndicatorBars size="xs" smallBars />}
            <PreviewRowLink pathnameUri={webappUriPath(releaseWebappUri)}>
              <h3
                className={clsx(
                  'w-full overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-xs font-medium text-base600',
                  isPlaying && 'ml-1',
                )}
              >
                {title}
              </h3>
            </PreviewRowLink>
          </div>
          <PreviewRowLink pathnameUri={webappUriPath(artistWebappUri)}>
            <span className="flex w-full items-center text-ellipsis whitespace-nowrap text-base-xs text-base500">
              {artistName}
            </span>
          </PreviewRowLink>
        </div>
      </div>
      {dateCollected && !!tooltipContent && (
        <CollectedBadge
          type={
            ownedGoldenEgg?.id ? 'goldenEgg' : getTierForNumber(ownedFirstNft?.tierNumber ?? null)
          }
          tooltipContent={tooltipContent}
        />
      )}
      <div className="hidden w-[60px] pr-[6px] text-right text-base-s text-base500 lt:block">
        {secondsToTime(trackDuration)}
      </div>
      <>
        <div className={clsx('h-8 w-8 flex-shrink-0', !isLiked && 'lg:[&_button]:hidden')}>
          <LikeReleaseButton
            className="group-hover/row:flex"
            releaseId={releaseId}
            disabled={false}
            source="Playlist Preview"
          />
        </div>
        <ThreeDotsDropdown
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          dropdownProps={{
            align: 'end',
          }}
        >
          {!tablet && <ViewSongMenuItem href={webappUriPath(releaseWebappUri)} />}
          <AddToShelfMenuItem
            releaseId={releaseId}
            disabled={!hasAudioAccess}
            source="Playlist Preview"
          />
          <AddToQueueMenuItem
            trackId={trackId}
            disabled={!trackId || !hasAudioAccess}
            emitAnalytics={() => {
              trackClickEvent({
                event: 'Add to Queue Clicked',
                properties: {
                  associationId: releaseId,
                  associationType: 'Release',
                  sourceComponent: 'Playlist Preview',
                },
              });
            }}
          />
          <ShareMenuItem releaseId={release.id} source="Playlist Preview" />
        </ThreeDotsDropdown>
      </>
    </div>
  );
};
